import React, { useEffect, useState } from 'react';
import DropdownSelect from '../Visualeffects/DropdownSelect';
import SolverTwoPanelLayout from './SolverTwoPanelLayout';

export default function DecompositionUI({ state, setState }) {
  const [activeMethod, setActiveMethod] = useState('');
  const [iterate, setIterate] = useState(true);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [maxIter, setMaxIter] = useState(10000);
  const [tolerance, setTolerance] = useState(1e-12);
  const [shift, setShift] = useState('0');
  const {
    matrixInput,
    result,
    error,
    loading,
    showInfo,
    pivoting,
    activeMethod: presetMethod,
    iterate: presetIterate,
    maxIter: presetMaxIter,
    tolerance: presetTolerance,
    shift: presetShift,
  } = state;

  const set = (key, value) => setState((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (typeof presetMethod === 'string' && presetMethod && presetMethod !== activeMethod) {
      setActiveMethod(presetMethod);
    }
  }, [presetMethod, activeMethod]);

  useEffect(() => {
    if (typeof presetIterate === 'boolean' && presetIterate !== iterate) {
      setIterate(presetIterate);
    }
  }, [presetIterate, iterate]);

  useEffect(() => {
    if (presetMaxIter !== undefined && presetMaxIter !== null && presetMaxIter !== maxIter) {
      setMaxIter(presetMaxIter);
    }
  }, [presetMaxIter, maxIter]);

  useEffect(() => {
    if (presetTolerance !== undefined && presetTolerance !== null && presetTolerance !== tolerance) {
      setTolerance(presetTolerance);
    }
  }, [presetTolerance, tolerance]);

  useEffect(() => {
    if (presetShift !== undefined && presetShift !== null) {
      const nextValue = typeof presetShift === 'number' ? String(presetShift) : presetShift;
      if (nextValue !== shift) {
        setShift(nextValue);
      }
    }
  }, [presetShift, shift]);

  const getResultBoxClasses = (res) => {
    if (!res) return 'bg-gray-50 border border-gray-200';
    if (res.converged !== undefined) {
      return res.converged
        ? 'bg-green-100 border border-green-300 text-green-800'
        : 'bg-red-100 border border-red-300 text-red-800';
    }
    return 'bg-green-100 border border-green-300 text-green-800';
  };

  //* ---------- Format helpers (three decimals, column-aligned) ---------- */

  // three decimals as before
  const fmt = (x) => {
    const n = Number(x);
    return Number.isFinite(n) ? n.toFixed(3) : String(x);
  };

  // Generic: from array or string -> matrix (Array<Array<number|string>>)
  const parseToMatrix = (input) => {
    try {
      if (Array.isArray(input)) {
        return input.map(row =>
          Array.isArray(row) ? row.map(v => (Number.isFinite(Number(v)) ? Number(v) : v)) : [input]
        );
      }
      if (typeof input === 'string') {
        const lines = input.split(/\r?\n/);
        const rows = lines
          .map(line => {
            // extract numbers per line (including e/E notation)
            const nums = [...line.matchAll(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/g)].map(m => Number(m[0]));
            if (nums.length) return nums;
            // fallback: ignore empty/other lines
            return null;
          })
          .filter(r => r && r.length);
        // if nothing found, try parsing as JSON
        if (rows.length === 0) {
          const maybe = JSON.parse(input);
          return parseToMatrix(maybe);
        }
        return rows;
      }
    } catch {/* fallthrough */ }
    // fallback: empty matrix
    return [];
  };

  // align columns with three decimals, right padding
  const formatAlignedMatrix = (matrixLike) => {
    const M = parseToMatrix(matrixLike);
    if (!Array.isArray(M) || M.length === 0) return String(matrixLike ?? '');

    // format all cells first
    const formatted = M.map(row => row.map(val => fmt(val)));

    // number of columns (maximum row length)
    const cols = Math.max(...formatted.map(r => r.length));

    // determine maximum width per column
    const colWidths = new Array(cols).fill(0);
    for (let r = 0; r < formatted.length; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = formatted[r][c] ?? '';
        colWidths[c] = Math.max(colWidths[c], String(cell).length);
      }
    }

    // assemble rows with each cell right-aligned
    const lines = formatted.map(row => {
      const padded = [];
      for (let c = 0; c < cols; c++) {
        const cell = row[c] ?? '';
        padded.push(String(cell).padStart(colWidths[c], ' '));
      }
      // a single space as column separator provides a compact, clearly aligned display
      return padded.join(' ');
    });

    return lines.join('\n');
  };

  // convenient wrappers (replacing old format* functions)
  const formatMatrixFromArray = (arr) => formatAlignedMatrix(arr);
  const formatMatrixString = (str) => formatAlignedMatrix(str);

  // format input matrix A directly from input field
  const renderMatrixAFromInput = () => {
    try {
      const A = JSON.parse(matrixInput);
      return formatAlignedMatrix(A);
    } catch {
      return formatAlignedMatrix(matrixInput);
    }
  };

  const handleDecompose = async () => {
    set('loading', true);
    set('error', null);
    set('result', null);

    try {
      const parsedShift = parseFloat(shift);
      const shiftValue = Number.isFinite(parsedShift) ? parsedShift : 0;
      const response = await fetch('https://api.numsolve.org/decompose', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matrix: JSON.parse(matrixInput),
          method: activeMethod,
          pivoting: pivoting,
          iterate: iterate,
          max_iter: iterate ? parseInt(maxIter) : undefined,
          tolerance: iterate ? parseFloat(tolerance) : undefined,
          shift: shiftValue,
        }),
      });

      const data = await response.json();

      if (data?.error) {
        set('error', data.error);
        set('loading', false);
        return; // ⬅️ kein Log schreiben bei Fehler
      }

      // Erfolgspfad
      set('result', data);

      const toAString = () => {
        try {
          const A = JSON.parse(matrixInput);
          if (Array.isArray(A)) {
            return A.map(row => (Array.isArray(row) ? row.join(' ') : row)).join('\n');
          }
        } catch {}
        return undefined;
      };

    const m = (activeMethod || '').toLowerCase();

    // determine log entries (iterative QR -> steps; otherwise snapshots)
    let logEntries = null;
    if (m === 'qr') {
      if (iterate) {
        logEntries = Array.isArray(data?.steps) ? data.steps : null;
      } else {
        const first = data?.steps?.[0] || {};
        logEntries = [{
          kind: 'qr',
          A_str: data?.input_matrix ?? first.A_str ?? toAString(),
          Q_str: first.Q_str ?? null,
          R_str: first.R_str ?? null,
        }];
      }
    } else if (m === 'lu') {
      logEntries = [{
        kind: 'lu',
        A_str: data?.input_matrix ?? toAString(),
        P_str: pivoting ? (data?.P_str ?? null) : null,
        L_str: data?.L_str ?? null,
        U_str: data?.R_str ?? null, // Backend liefert U in R_str
      }];
    } else if (m === 'cholesky') {
      logEntries = [{
        kind: 'cholesky',
        A_str: data?.input_matrix ?? toAString(),
        L_str: data?.L_str ?? null,
      }];
    } else if (m === 'svd') {
      logEntries = [{
        kind: 'svd',
        A_str: data?.input_matrix ?? toAString(),
        U_str: data?.U_str ?? null,
        S_str: data?.S_str ?? null,
        Vt_str: data?.Vt_str ?? null,
        singular_values: Array.isArray(data?.singular_values) ? data.singular_values : null,
      }];
    }

    const recentEntry = {
      timestamp: new Date().toISOString(),
      method: `decomposition-${m}`,
      input: {
        matrix: JSON.parse(matrixInput),
        method: activeMethod,
        pivoting,
        iterate,
        max_iter: iterate ? parseInt(maxIter) : undefined,
        tolerance: iterate ? parseFloat(tolerance) : undefined,
        shift: shiftValue,
      },
      summary: {
        eigenvalues: Array.isArray(data?.eigenvalues) ? data.eigenvalues : undefined,
        converged: data?.converged,
        iterations: data?.iterations,
        final_residual: data?.final_residual,
        shift: shiftValue,
      },
      log: Array.isArray(logEntries) && logEntries.length ? logEntries : null,
      log_file: data?.log_file || null,
    };

    const existing = JSON.parse(localStorage.getItem('calcLogs') || '[]');
    const updated = [recentEntry, ...existing].slice(0, 100);
    localStorage.setItem('calcLogs', JSON.stringify(updated));
    window.dispatchEvent(new Event('calcLogsUpdated')); // exactly once

    } catch (err) {
      set('error', 'Backend is not reachable.');
    }

    set('loading', false);
  };

  const inputCard = (
    <div className="rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] backdrop-blur">
      <label className="mb-3 block text-sm font-bold text-gray-700">Matrix</label>
      <div className="input-wrapper input-pulse-rose">
        <textarea
          value={matrixInput}
          onChange={(e) => set('matrixInput', e.target.value)}
          placeholder="e.g. [[12, -51, 4], [6, 167, -68], [-4, 24, -41]]"
          rows={5}
          className="w-full resize-none rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 focus:outline-none"
        />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <div className="min-w-[200px] flex-grow">
          <DropdownSelect
            label="Decomposition Method"
            value={activeMethod}
            color="pink"
            onChange={(val) => {
              setActiveMethod(val);
              set('activeMethod', val);
            }}
            options={[
              { value: 'lu', label: 'LU Decomposition' },
              { value: 'cholesky', label: 'Cholesky' },
              { value: 'qr', label: 'QR Decomposition' },
              { value: 'svd', label: 'Singular Value Decomposition' },
            ]}
          />
        </div>

        {activeMethod === 'qr' && (
          <div className="flex items-center gap-2 pt-[28px]">
            <input
              type="checkbox"
              checked={iterate}
              onChange={(e) => {
                const checked = e.target.checked;
                setIterate(checked);
                set('iterate', checked);
              }}
              className="h-4 w-4"
            />
            <label className="text-sm text-gray-700">Iterative (for Eigenvalues)</label>
          </div>
        )}

        {activeMethod === 'lu' && (
          <div className="flex items-center gap-2 pt-[28px]">
            <input
              type="checkbox"
              id="pivoting"
              checked={pivoting}
              onChange={(e) => set('pivoting', e.target.checked)}
              className="h-4 w-4"
            />
            <label htmlFor="pivoting" className="text-sm text-gray-700">
              Enable Pivoting (PA = LU)
            </label>
          </div>
        )}
      </div>

        {activeMethod === 'qr' && iterate && (
        <div className="mt-4 flex w-full flex-wrap gap-4">
          <div className="min-w-[160px] flex-1">
            <label className="mb-1 block text-sm font-bold text-gray-700">Max Iterations</label>
            <div className="input-wrapper input-wrapper-tight input-pulse-rose">
              <input
                type="text"
                value={maxIter}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!Number.isNaN(value)) {
                    setMaxIter(value);
                    set('maxIter', value);
                  }
                }}
                placeholder="e.g. 1000"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-sm text-gray-800"
              />
            </div>
          </div>

          <div className="min-w-[160px] flex-1">
            <label className="mb-1 block text-sm font-bold text-gray-700">Tolerance</label>
            <div className="input-wrapper input-wrapper-tight input-pulse-rose">
              <input
                type="text"
                value={tolerance}
                onChange={(e) => {
                  const val = e.target.value;
                  setTolerance(val);
                  set('tolerance', val);
                }}
                placeholder="e.g. 1e-12"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-sm text-gray-800"
              />
            </div>
          </div>

          <div className="min-w-[160px] flex-1">
            <label className="mb-1 block text-sm font-bold text-gray-700">Shift</label>
            <div className="input-wrapper input-wrapper-tight input-pulse-rose">
              <input
                type="text"
                value={shift}
                onChange={(e) => {
                  const val = e.target.value;
                  setShift(val);
                  set('shift', val);
                }}
                placeholder="e.g. 0"
                className="w-full rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-sm text-gray-800"
              />
            </div>
          </div>
        </div>
      )}

      <div className="mt-6">
        <button className="button button-pink" onClick={handleDecompose} disabled={loading}>
          {loading ? 'Decomposing…' : 'Solve'}
        </button>
      </div>
    </div>
  );

  const outputCard = (
    <div
      className={`rounded-3xl border border-white/30 bg-white/80 p-8 shadow-[0_30px_80px_-45px_rgba(15,20,43,0.95)] transition-all duration-300 ${getResultBoxClasses(result)} ${
        result && !error && (result.converged === undefined || result.converged) ? 'result-success' : ''
      }`}
    >
      <h2 className="mb-4 text-xl font-semibold text-gray-900">Results</h2>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-800">
          <strong>Error:</strong> {error}
        </div>
      )}

      {!result && !error && !loading && (
        <div className="text-sm text-gray-500">No results yet. Please enter a matrix.</div>
      )}

      {result && (
        <div className="space-y-4 text-sm text-gray-800">
          {activeMethod === 'qr' && (
            <>
              {(result.input_matrix || matrixInput) && (
                <div>
                  <p className="font-semibold">Input Matrix A:</p>
                  <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-gray-300 bg-white p-3">
                    {result.input_matrix ? formatMatrixString(result.input_matrix) : renderMatrixAFromInput()}
                  </pre>
                </div>
              )}

              {iterate ? (
                <>
                  {Array.isArray(result.eigenvalues) && result.eigenvalues.length > 0 && (
                    <div className="mt-4 text-sm">
                      <p className="font-semibold">Eigenvalues:</p>
                      <pre className="rounded border border-pink-200 bg-pink-50 p-3 text-pink-800">
                        [{result.eigenvalues.map((v) => fmt(v)).join(', ')}]
                      </pre>
                    </div>
                  )}
                  <div className="mt-4 space-y-1 text-sm">
                    <p>
                      <strong>Converged:</strong> {result.converged ? 'Yes' : 'No'}
                    </p>
                    <p>
                      <strong>Final Residual:</strong> {result.final_residual?.toExponential?.(4) ?? 'N/A'}
                    </p>
                    <p>
                      <strong>Total Iterations:</strong> {result.iterations}
                    </p>
                    <p>
                      <strong>Shift:</strong> {fmt(result.shift ?? shift)}
                    </p>
                  </div>

                  {Array.isArray(result.steps) && result.steps.length > 0 && (
                    <div className="relative mt-6">
                      <h3 className="mb-2 text-md font-semibold text-gray-800">QR Iterations</h3>
                      <div className={`space-y-6 font-mono text-sm text-gray-800 transition-all duration-500 ease-in-out ${showAllSteps ? '' : 'max-h-[560px] overflow-hidden'}`}>
                        {result.steps.map((step, i) => (
                          <div key={i} className="border-b pb-4">
                            <p className="mb-1 font-semibold">Step {step.iteration}</p>
                            <p>Q:</p>
                            <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                              {formatMatrixString(step.Q_str)}
                            </pre>
                            <p className="mt-2">R:</p>
                            <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                              {formatMatrixString(step.R_str)}
                            </pre>
                            <p className="mt-2">A:</p>
                            <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                              {formatMatrixString(step.A_str)}
                            </pre>
                          </div>
                        ))}
                      </div>
                      {result.steps.length > 3 && (
                        <div className="relative z-20 mt-4 text-center">
                          <button
                            onClick={() => setShowAllSteps((prev) => !prev)}
                            className="inline-block rounded-full border border-pink-200 bg-white px-4 py-1 text-sm font-medium text-pink-800 shadow-sm transition-all hover:bg-pink-50"
                          >
                            {showAllSteps ? 'Hide steps' : 'Show more steps'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <p className="font-semibold">Q Matrix:</p>
                  <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                    {formatMatrixString(result.Q_str)}
                  </pre>
                  <p className="mt-2 font-semibold">R Matrix:</p>
                  <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                    {formatMatrixString(result.R_str)}
                  </pre>
                </>
              )}
            </>
          )}

          {activeMethod === 'lu' && (
            <>
              <p className="font-semibold">L Matrix:</p>
              <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                {formatMatrixString(result.L_str)}
              </pre>
              <p className="mt-2 font-semibold">U Matrix:</p>
              <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                {formatMatrixString(result.R_str)}
              </pre>
              {pivoting && result.P_str && (
                <div className="mt-2">
                  <p className="font-semibold">Permutation Matrix P:</p>
                  <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                    {formatMatrixString(result.P_str)}
                  </pre>
                </div>
              )}
            </>
          )}

          {activeMethod === 'cholesky' && (
            <>
              <p className="font-semibold">L Matrix:</p>
              <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                {formatMatrixString(result.L_str)}
              </pre>
            </>
          )}

          {activeMethod === 'svd' && (
            <>
              {(result.input_matrix || matrixInput) && (
                <div>
                  <p className="font-semibold">Input Matrix A:</p>
                  <pre className="overflow-x-auto whitespace-pre-wrap rounded border border-gray-300 bg-white p-3">
                    {result.input_matrix ? formatMatrixString(result.input_matrix) : renderMatrixAFromInput()}
                  </pre>
                </div>
              )}

              <p className="font-semibold">U Matrix:</p>
              <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                {formatMatrixString(result.U_str)}
              </pre>

              <p className="mt-2 font-semibold">Σ Matrix:</p>
              <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                {formatMatrixString(result.S_str)}
              </pre>

              <p className="mt-2 font-semibold">Vᵀ Matrix:</p>
              <pre className="whitespace-pre rounded border border-gray-300 bg-white p-3 font-mono">
                {formatMatrixString(result.Vt_str)}
              </pre>

              {Array.isArray(result.singular_values) && result.singular_values.length > 0 && (
                <div className="mt-3 text-sm">
                  <p className="font-semibold">Singular Values:</p>
                  <pre className="rounded border border-pink-200 bg-pink-50 p-3 text-pink-800">
                    [{result.singular_values.map((v) => fmt(v)).join(', ')}]
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <SolverTwoPanelLayout
      loading={loading}
      inputCard={inputCard}
      outputCard={outputCard}
    />
  );
}
